import { useCallback, useEffect, useState } from "react";
import { MdRefresh, MdSend } from "react-icons/md";
import { getApiErrorMessage } from "api";
import { getAudience } from "components/assessment/audiences";
import AIWeeklySummary from "components/assessment/checkin/AIWeeklySummary";
import BlockersPanel from "components/assessment/checkin/BlockersPanel";
import CheckInMetrics from "components/assessment/checkin/CheckInMetrics";
import ResponsesTable from "components/assessment/checkin/ResponsesTable";
import StoriesHighlights from "components/assessment/checkin/StoriesHighlights";
import TeamHealthHeatmap from "components/assessment/checkin/TeamHealthHeatmap";
import Button, { IconButton } from "components/button";
import Select from "components/fields/Select";
import { PageContainer, PageHeader } from "components/page";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import notify from "lib/notify";
import { track } from "lib/mixpanel";
import { formatDate } from "utils/format";

const WEEKS_STEP = 8;

function LoadingSkeleton() {
  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 w-full rounded-card" />
        ))}
      </div>
      <Skeleton className="h-96 w-full rounded-card" />
      <Skeleton className="h-32 w-full rounded-card" />
      <Skeleton className="h-72 w-full rounded-card" />
    </>
  );
}

export default function WeeklyCheckIn({ audience }) {
  const config = getAudience(audience);
  const { service, labels, events } = config;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekSummaries, setWeekSummaries] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [sending, setSending] = useState(false);
  const [weeksToLoad, setWeeksToLoad] = useState(WEEKS_STEP);
  const [pulseQuestions, setPulseQuestions] = useState([]);

  useEffect(() => {
    track(events.weeklyCheckInViewed);
  }, [events.weeklyCheckInViewed]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await service.getCheckInOverview({ weeks: weeksToLoad });
      if (res.success && res.data) {
        const summaries = res.data.weekSummaries || [];
        setWeekSummaries(summaries);
        setSelectedWeek((current) => current || summaries[0]?.weekOf || "");
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [service, weeksToLoad]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    service
      .getPulseQuestions()
      .then((res) => {
        if (res.success) setPulseQuestions(res.data || []);
      })
      // Custom question labels are optional; the responses fall back to default labels.
      .catch(() => setPulseQuestions([]));
  }, [service]);

  const handleSendCheckIn = async () => {
    setSending(true);
    try {
      const response = await service.sendCheckInNow();
      if (response.success) {
        notify.success(
          response.data.message || `Check-in sent to ${response.data.sentCount} team members`
        );
        fetchData();
      } else {
        notify.error(response.error || "Could not send check-in");
      }
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not send check-in"));
    } finally {
      setSending(false);
    }
  };

  const currentSummary =
    weekSummaries.find((s) => s.weekOf === selectedWeek) || weekSummaries[0];
  const currentResponses = currentSummary?.responses || [];

  const header = (
    <PageHeader
      title="Weekly Check-In"
      description={`Track weekly check-in responses and pulse for your ${labels.pluralLower}`}
      actions={
        <>
          {weekSummaries.length > 0 && (
            <Select
              aria-label="Week"
              className="w-full sm:w-56"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              options={weekSummaries.map((s) => ({
                value: s.weekOf,
                label: `Week of ${formatDate(s.weekOf)}`,
              }))}
            />
          )}
          <IconButton
            variant="secondary"
            icon={<MdRefresh />}
            label="Refresh"
            loading={loading && weekSummaries.length > 0}
            onClick={fetchData}
          />
          <Button leftIcon={<MdSend />} loading={sending} onClick={handleSendCheckIn}>
            Send Check-In Now
          </Button>
        </>
      }
    />
  );

  let content;
  if (error) {
    content = (
      <ErrorState
        message={getApiErrorMessage(error, "Could not load check-in data")}
        onRetry={fetchData}
      />
    );
  } else if (loading && weekSummaries.length === 0) {
    content = <LoadingSkeleton />;
  } else if (weekSummaries.length === 0) {
    content = (
      <EmptyState
        icon={MdSend}
        title="No Check-In Data Yet"
        description="Send your first weekly check-in to start tracking team pulse."
      />
    );
  } else {
    content = (
      <>
        <CheckInMetrics config={config} summary={currentSummary} />
        <ResponsesTable
          config={config}
          responses={currentResponses}
          allSummaries={weekSummaries}
          selectedWeek={selectedWeek}
          onUpdate={fetchData}
          pulseQuestions={pulseQuestions}
        />
        <AIWeeklySummary summary={currentSummary} />
        <TeamHealthHeatmap
          config={config}
          summaries={weekSummaries}
          weeksLoaded={weeksToLoad}
          onLoadMore={() => setWeeksToLoad((prev) => prev + WEEKS_STEP)}
          onLoadLess={() => setWeeksToLoad((prev) => Math.max(WEEKS_STEP, prev - WEEKS_STEP))}
        />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <BlockersPanel summary={currentSummary} />
          <StoriesHighlights summary={currentSummary} />
        </div>
      </>
    );
  }

  return (
    <PageContainer>
      {header}
      {content}
    </PageContainer>
  );
}
